import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploader } from "react-drag-drop-files";
import { CloudArrowUpIcon, DocumentTextIcon, MusicalNoteIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatFileSize, validateFile, FILE_VALIDATION } from '../../utils/pipelineHelpers';

function PipelineFileUpload({ files, onFilesChange, disabled }) {
    const { t } = useTranslation();
    const [dragActive, setDragActive] = useState(false);

    const fileTypes = ["mp3", "wav", "m4a", "pdf"];

    const handleFileUpload = (newFiles) => {
        // Convert FileList to Array if needed
        const fileArray = Array.isArray(newFiles) ? newFiles : Array.from(newFiles);
        
        // Filter and validate files
        const validFiles = [];
        const invalidFiles = [];

        fileArray.forEach(file => {
            const errors = validateFile(file);
            if (errors.length === 0) {
                validFiles.push(file);
            } else {
                invalidFiles.push({ file, errors });
            }
        });

        // Show validation errors
        if (invalidFiles.length > 0) {
            const errorMessage = invalidFiles.map(({ file, errors }) =>
                `${file.name}: ${errors.join(', ')}`
            ).join('\n');
            alert(`${t('ResearchPipeline.fileUpload.someFilesInvalid')}\n${errorMessage}`);
        }

        // Check total file count
        const newTotalFiles = files.length + validFiles.length;
        if (newTotalFiles > FILE_VALIDATION.MAX_FILES) {
            const allowedNewFiles = FILE_VALIDATION.MAX_FILES - files.length;
            validFiles.splice(allowedNewFiles);
            alert(t('ResearchPipeline.fileUpload.maxFilesExceeded', { max: FILE_VALIDATION.MAX_FILES, allowed: allowedNewFiles }));
        }

        // Add valid files
        if (validFiles.length > 0) {
            onFilesChange([...files, ...validFiles]);
        }
    };

    const removeFile = (indexToRemove) => {
        const newFiles = files.filter((_, index) => index !== indexToRemove);
        onFilesChange(newFiles);
    };

    const getFileIcon = (file) => {
        const extension = file.name.split('.').pop().toLowerCase();
        
        if (['mp3', 'wav', 'm4a'].includes(extension)) {
            return <MusicalNoteIcon className="w-6 h-6 text-blue-500" />;
        } else if (extension === 'pdf') {
            return <DocumentTextIcon className="w-6 h-6 text-red-500" />;
        }
        
        return <DocumentTextIcon className="w-6 h-6 text-gray-500" />;
    };

    const getTotalSize = () => {
        return files.reduce((total, file) => total + file.size, 0);
    };

    return (
        <div className="space-y-4">
            
            {/* Upload Area */}
            <FileUploader
                multiple={true}
                handleChange={handleFileUpload}
                name="files"
                types={fileTypes}
                maxSize={FILE_VALIDATION.MAX_FILE_SIZE / (1024 * 1024)} // Convert to MB
                disabled={disabled}
                onDraggingStateChange={setDragActive}
            >
                <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                        dragActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                    <CloudArrowUpIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    
                    {dragActive ? (
                        <p className="text-blue-600 font-medium">{t('ResearchPipeline.fileUpload.dropFilesHere')}</p>
                    ) : (
                        <div>
                            <p className="text-gray-600 font-medium mb-2">
                                {t('ResearchPipeline.fileUpload.dropOrClickToUpload')}
                            </p>
                            <p className="text-sm text-gray-500">
                                {t('ResearchPipeline.fileUpload.supportedFormats')} • {t('ResearchPipeline.fileUpload.maxFileSize', { size: formatFileSize(FILE_VALIDATION.MAX_FILE_SIZE) })} • {t('ResearchPipeline.fileUpload.maxFiles', { count: FILE_VALIDATION.MAX_FILES })}
                            </p>
                        </div>
                    )}
                </div>
            </FileUploader>

            {/* File List */}
            {files.length > 0 && (
                <div className="space-y-3">
                    <div className="flex justify-between items-center">
                        <h4 className="font-medium text-gray-900">
                            {t('ResearchPipeline.fileUpload.selectedFiles', { current: files.length, max: FILE_VALIDATION.MAX_FILES })}
                        </h4>
                        <p className="text-sm text-gray-500">
                            {t('ResearchPipeline.fileUpload.total', { size: formatFileSize(getTotalSize()) })}
                        </p>
                    </div>
                    
                    <div className="space-y-2">
                        {files.map((file, index) => (
                            <div
                                key={`${file.name}-${index}`}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                            >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {getFileIcon(file)}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {file.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {formatFileSize(file.size)}
                                        </p>
                                    </div>
                                </div>
                                
                                {!disabled && (
                                    <button
                                        type="button"
                                        onClick={() => removeFile(index)}
                                        className="text-gray-400 hover:text-red-500 p-1 ml-2"
                                    >
                                        <XMarkIcon className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* File Validation Info */}
            <div className="text-xs text-gray-500 space-y-1">
                <p>• {t('ResearchPipeline.fileUpload.supportedFormatsInfo', { formats: FILE_VALIDATION.ALLOWED_EXTENSIONS.join(', ') })}</p>
                <p>• {t('ResearchPipeline.fileUpload.maxFileSizeInfo', { size: formatFileSize(FILE_VALIDATION.MAX_FILE_SIZE) })}</p>
                <p>• {t('ResearchPipeline.fileUpload.maxFilesInfo', { count: FILE_VALIDATION.MAX_FILES })}</p>
                <p>• {t('ResearchPipeline.fileUpload.audioFilesInfo')}</p>
                <p>• {t('ResearchPipeline.fileUpload.pdfFilesInfo')}</p>
            </div>
        </div>
    );
}

export default PipelineFileUpload;